import {Component, Input, Output, EventEmitter, ViewEncapsulation} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TplSource, TplConfig, colorToVar, CONFIRMATION_COLORS, DEFAULT_CONFIRMATION_COLOR} from '../cml-types';
import {HighlightPipe} from '../highlight.pipe';

@Component({
  selector: 'cml-template-row',
  standalone: true,
  imports: [FormsModule, HighlightPipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="liTpl" [class.tpl-dimmed]="tpl.hidden === true || isDeleted()">
      <div class="contcheckTpl">
        <input type="checkbox" class="inCheckTpl" [id]="'tpl' + tpl._id"
               [checked]="config?.checked"
               [disabled]="isManagedByBackstage()"
               (change)="toggle.emit(tpl._id)">
        <label class="labCheckTpl" [for]="'tpl' + tpl._id">
          <i class="fas fa-check icCheckTpl"></i>
        </label>
      </div>
      <div class="nameTpl" [innerHTML]="tpl.name | highlight:searchTerm"></div>
      @if (tpl.hidden) {
        <span class="tpl-state-badge">Masqué</span>
      }
      @if (isDeleted()) {
        <span class="tpl-state-badge">Supprimé</span>
      }
      @if (isManagedByBackstage()) {
        <span class="tpl-state-badge" title="Type d'accès modifiable uniquement depuis Wilout Backstage">Géré par Backstage</span>
      }
      @if (tpl.controlLabel) {
        <div class="tpl-label">
          @if (tpl.controlColor) {
            <span class="tpl-label__dot" [style.background-color]="colorToVar(tpl.controlColor)"></span>
          }
          <span class="tpl-label__text">{{ tpl.controlLabel }}</span>
        </div>
      }
      <div class="priceTpl">{{ formatPrice(tpl.prixVente) }}</div>
      <div class="colorConfTpl" [class.colorConfTplOff]="!config?.checked">
        @if (!colorPickerOpen) {
          <button type="button" class="colorDotZone"
                  title="Couleur de confirmation"
                  [disabled]="isManagedByBackstage()"
                  (click)="colorPickerOpen = true">
            <span class="colorDot colorDotCurrent" [style.background-color]="currentColor()"></span>
          </button>
        } @else {
          @for (c of confirmationColors; track c.value) {
            <button type="button" class="colorDotZone"
                    [title]="c.name"
                    (click)="selectColor(c.value)">
              <span class="colorDot"
                    [class.colorDotOn]="isColorSelected(c.value)"
                    [style.background-color]="c.value"></span>
            </button>
          }
          <button type="button" class="colorPickerClose" title="Fermer" (click)="colorPickerOpen = false">
            <i class="fas fa-times"></i>
          </button>
        }
      </div>
      <div class="nfcModeTpl">
        <select class="inNfcTpl"
                [class.inNfcTplOff]="!config?.checked"
                [value]="config?.nfcMode || 'inherit'"
                [disabled]="isManagedByBackstage()"
                (change)="nfcChange.emit({id: tpl._id, value: $any($event.target).value})">
          <option value="inherit">Par défaut</option>
          <option value="disabled">Désactivé</option>
          <option value="optional">Optionnel</option>
          <option value="required">Obligatoire</option>
        </select>
      </div>
      <div class="nbScanTpl">
        <input class="inScanTpl"
               [class.inScanTplOff]="!config?.checked"
               type="number" min="0"
               [value]="config?.scanNbr ?? 0"
               [disabled]="isManagedByBackstage()"
               (change)="scanChange.emit({id: tpl._id, value: +$any($event.target).value})">
      </div>
    </div>
  `
})
export class CmlTemplateRowComponent {
  @Input() tpl!: TplSource;
  @Input() config: TplConfig | undefined;
  @Input() searchTerm = '';
  @Output() toggle = new EventEmitter<string>();
  @Output() nfcChange = new EventEmitter<{id: string; value: string}>();
  @Output() scanChange = new EventEmitter<{id: string; value: number}>();
  @Output() colorChange = new EventEmitter<{id: string; value: string}>();

  readonly colorToVar = colorToVar;
  readonly confirmationColors = CONFIRMATION_COLORS;
  readonly defaultColor = DEFAULT_CONFIRMATION_COLOR;

  colorPickerOpen = false;

  /** Tarif supprimé, encore listé parce que des billets ont été vendus (#5769). */
  isDeleted(): boolean {
    return this.tpl.statut === 'garbage' || this.tpl.status === 'garbage';
  }

  /** Type d'accès géré par Wilout Backstage (#9400) : affiché, en lecture seule. */
  isManagedByBackstage(): boolean {
    return this.config?.accreditation === true;
  }

  currentColor(): string {
    return this.config?.colorConfirmation || this.defaultColor;
  }

  isColorSelected(value: string): boolean {
    return this.currentColor() === value;
  }

  selectColor(value: string): void {
    this.colorChange.emit({id: this.tpl._id, value: value === this.defaultColor ? '' : value});
    this.colorPickerOpen = false;
  }

  formatPrice(price?: number | string): string {
    if (price == null) return '-';
    const n = parseFloat(String(price));
    if (isNaN(n)) return '-';
    return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ',') + ' €';
  }
}
